export type ErrorType =
  | 'badRequest'
  | 'internalError'
  | 'notFound'
  | 'validationError'
  | 'unauthorized'
  | 'forbidden'
  | 'conflict'
  | 'badGateway';

const ERROR_STATUS_CODE_MAP: Record<ErrorType, number> = {
  badRequest: 400,
  unauthorized: 401,
  forbidden: 403,
  notFound: 404,
  conflict: 409,
  validationError: 422,
  internalError: 500,
  badGateway: 502,
};

const ERROR_NAME_MAP: Record<ErrorType, string> = {
  badRequest: 'Bad Request',
  unauthorized: 'Unauthorized',
  forbidden: 'Forbidden',
  notFound: 'Not Found',
  conflict: 'Conflict',
  validationError: 'Validation Error',
  internalError: 'Internal Server Error',
  badGateway: 'Bad Gateway',
};

const ERROR_TYPE_BY_NAME = Object.fromEntries(
  Object.entries(ERROR_NAME_MAP).map(([type, name]) => [name, type as ErrorType]),
);

export type AppErrorDetails = {
  // Optional user facing error messages with the root cause of the error.
  root?: string[];

  // Optional user facing error messages for specific fields.
  fields?: Record<string, string[]>;

  // Allow additional properties
  [key: string]: unknown;
};

export type AppErrorOptions = {
  cause?: unknown;
  statusCode?: number;
  details?: AppErrorDetails;
  skipMessageFormatting?: boolean;
  skipLogging?: boolean;
};

export class AppError extends Error {
  readonly type: ErrorType;
  readonly cause?: unknown;
  readonly statusCode: number;
  readonly details?: AppErrorDetails;
  readonly rollbarContext?: unknown;
  readonly skipLogging: boolean;

  constructor(
    errorType: ErrorType,
    message: string,
    { cause, details, statusCode, skipMessageFormatting, skipLogging }: AppErrorOptions = {},
  ) {
    super(skipMessageFormatting ? message : formatAppErrorMessage(message, cause));
    this.type = errorType;
    this.name = ERROR_NAME_MAP[errorType];
    this.details = {
      ...(cause && cause instanceof AppError ? cause.details : {}),
      ...details,
    };
    this.cause = cause;
    this.skipLogging = skipLogging || false;

    if (statusCode) {
      this.statusCode = statusCode;
    } else {
      this.statusCode = ERROR_STATUS_CODE_MAP[errorType] || 500;
    }

    this.rollbarContext = {
      type: this.type,
      statusCode: this.statusCode,
      ...(this.details || {}),
    };
  }

  /**
   * Determines whether the operation that caused this error can be retried.
   *
   * Client errors (4xx) are not retryable as they require user action to resolve.
   */
  canRetry(): boolean {
    return (
      this.type !== 'badRequest' &&
      this.type !== 'unauthorized' &&
      this.type !== 'forbidden' &&
      this.type !== 'notFound' &&
      this.type !== 'conflict' &&
      this.type !== 'validationError'
    );
  }

  /**
   * Determines whether the error should be logged based on its type.
   *
   * We should add rules here to avoid logging unwanted errors.
   */
  shouldLog(): boolean {
    if (this.skipLogging) return false;

    // Avoid logging any of the 400 range errors
    return (
      this.type !== 'badRequest' &&
      this.type !== 'unauthorized' &&
      this.type !== 'forbidden' &&
      this.type !== 'notFound' &&
      this.type !== 'conflict' &&
      this.type !== 'validationError'
    );
  }

  toJSON() {
    switch (this.type) {
      case 'validationError': {
        const validationErrorDetails = this.details as ValidationErrorDetails;
        const fields = validationErrorDetails.fields
          ? Object.entries(validationErrorDetails.fields).reduce(
              (acc, [key, value]) => {
                if (!value || value.length === 0) return acc;
                const fields = acc || {};
                fields[key] = value[0];
                return fields;
              },
              undefined as Record<string, string> | undefined,
            )
          : undefined;

        return {
          error: this.name,
          message: this.message,
          validation: {
            root: validationErrorDetails?.root?.[0],
            fields,
          },
          statusCode: this.statusCode,
        };
      }

      case 'internalError': {
        // Avoid leaking internal error details
        return {
          error: this.name,
          message: 'An unexpected error occurred',
          statusCode: this.statusCode,
        };
      }

      default: {
        return {
          error: this.name,
          message: this.message,
          statusCode: this.statusCode,
        };
      }
    }
  }

  /**
   * Returns a user-friendly error message.
   * This is used to display errors in the UI.
   *
   * @params fallbackMessage - Optional fallback message if no specific error details are available.
   */
  getUserMessage(fallbackMessage?: string): string {
    // Priority: root errors > field errors > general message
    if (this.details?.root && this.details.root?.length > 0) {
      return this.details.root[0];
    }

    if (this.details?.fields) {
      const fieldErrors = Object.values(this.details.fields).flat();
      if (fieldErrors.length > 0) {
        return fieldErrors[0];
      }
    }

    if (fallbackMessage) {
      return fallbackMessage;
    }

    // We only return the internal message if no user-friendly message is available.
    return 'Erro inesperado';
  }

  static fromMastra(error: unknown, message: string, options?: Omit<AppErrorOptions, 'cause'>) {
    const rawMessage = String(error);

    const type: ErrorType = rawMessage.includes('status: 404')
      ? 'notFound'
      : rawMessage.includes('status: 401')
        ? 'unauthorized'
        : rawMessage.includes('status: 403')
          ? 'forbidden'
          : rawMessage.includes('status: 409')
            ? 'conflict'
            : rawMessage.includes('status: 422')
              ? 'validationError'
              : 'internalError';

    return new AppError(type, message, { ...options, cause: error });
  }

  static fromJSON(json: Record<string, unknown>, fallbackMessage: string): AppError {
    const type = (ERROR_TYPE_BY_NAME[json.error as string] || 'internalError') as ErrorType;
    const message = (json.message || fallbackMessage) as string;

    const details: AppErrorDetails = {};
    if (json.validation) {
      const validation = json.validation as { root?: string; fields?: Record<string, string> };
      const root = validation.root as string;
      details.root = root ? [root] : undefined;
      if (validation.fields) {
        details.fields = Object.fromEntries(
          Object.entries(validation.fields).map(([key, value]) => [key, [value]]),
        );
      }
    }

    return new AppError(type, message, {
      statusCode: json.statusCode as number,
      details,
    });
  }

  static userMessageOrFallback(error: unknown, fallbackMessage: string): string {
    if (error instanceof AppError) {
      return error.getUserMessage(fallbackMessage);
    }

    return fallbackMessage;
  }
}

export type ValidationErrorDetails = {
  root?: string[];
  fields?: Record<string, string[]>;
};

/**
 * Formats an error message by appending additional details from the original error if provided.
 *
 * @param message The main error message to be formatted.
 * @param originalError An optional parameter representing the original error.
 * @example
 * formatAppErrorMessage('Something went wrong', new Error('timeout')) // "Something went wrong: timeout"
 * formatAppErrorMessage('Something went wrong') // "Something went wrong"
 */
function formatAppErrorMessage(message: string, originalError?: unknown): string {
  if (!originalError) return message;
  if (typeof originalError === 'object' && 'message' in originalError) {
    return `${message} - ${originalError.message}`;
  }
  return `${message} - ${originalError}`;
}
